import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon, Text } from "@zorni/components-lib";

import { useAdmin } from "../../hooks/useAdmin";
import { useAdminAccessControl } from "../../hooks/useAdminAccessControl";
import { useOrganismeData } from "../../components/organisme/hooks/useOrganismeData";
import { ORGANISME_STATUS } from "../model/enum";
import Preloader from "../../../common/preLoader/PreLoader";

import styles from "./AccountActivationRequired.module.css";

// Même style neutre pour le CTA des 3 étapes (y compris l'étape 1) — via les
// props d'override de Button (background/hoverColor/textColor) pour
// respecter le design system sans toucher à zorni-components-lib.
// NOTE : ces overrides passent par les custom properties --btn-bg/--btn-fg
// consommées par la classe de base de Button — variant="secondary" les
// shadow avec des valeurs littérales et ignorerait donc ces props ; on reste
// volontairement sur variant="primary".
const CTA_PROPS = {
  variant: "primary",
  background: "var(--bg-muted)",
  hoverColor: "var(--gray-200)",
  textColor: "var(--text-muted)",
};

const StepCard = ({
  status,
  ctaIcon,
  stepLabel,
  tagLabel,
  title,
  description,
  ctaLabel,
  onCta,
}) => {
  const isDone = status === "done";
  const isLocked = status === "locked";

  return (
    <div className={[styles.step, styles[`step-${status}`]].join(" ")}>
      <div className={styles.stepMain}>
        <span className={[styles.stepCheck, styles[`stepCheck-${status}`]].join(" ")}>
          {isDone && <Icon name="iconCheck" size="sm" color="inherit" />}
        </span>
        <div>
          <div className={styles.stepMeta}>
            <Text
              as="span"
              size="xs"
              weight="bold"
              color={status === "current" ? "warning" : "muted"}
              className={styles.stepLabel}
            >
              {stepLabel}
            </Text>
            <span className={[styles.tag, styles[`tag-${status}`]].join(" ")}>{tagLabel}</span>
          </div>
          <Text as="h5" size="md" weight="bold">
            {title}
          </Text>
          <Text as="p" size="xs" color="muted" className={styles.stepDescription}>
            {description}
          </Text>
        </div>
      </div>

      {/* Étape validée : le bouton reste, il mène à ce qui a déjà été créé. */}
      <Button
        {...CTA_PROPS}
        size="small"
        iconName={ctaIcon}
        iconPosition="right"
        label={ctaLabel}
        onClick={onCta}
        disabled={isLocked}
        className={styles.stepCta}
      />
    </div>
  );
};

/**
 * Remplace WarningComponent/RequiredAction : composant unique d'activation du
 * compte, calculant lui-même les prérequis via useAdminAccessControl (même
 * source de vérité que la navigation et le routage /admin). Parcours
 * strictement séquentiel : une étape n'est "current" (actionnable) que si
 * toutes les précédentes sont "done" ; sinon elle est "locked".
 */
const AccountActivationRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isPersonActivated,
    hasOrganization,
    hasEstablishments,
    hasValidEstablishment,
  } = useAdminAccessControl();

  // Le statut de l'organisme se lit ici, à la demande, et non dans
  // useAdminAccessControl : ce composant n'est monté que lorsque l'accès est
  // bloqué, donc un utilisateur déverrouillé n'appelle jamais GET_ORGANIZATION
  // à l'arrivée sur /admin. cache-first : la requête est mutualisée avec la
  // page Organisme, qui réécrit le cache après chaque enregistrement.
  const { organization } = useAdmin();
  const { organisme, loading: organismeLoading } = useOrganismeData(organization?.entityId);
  const isOrganismeCompleted = organisme?.status === ORGANISME_STATUS.COMPLETED;

  const step1Done = isPersonActivated;
  // Étape 2 validée uniquement quand l'organisme existe ET a le statut COMPLETED.
  const step2Done = hasOrganization && isOrganismeCompleted;
  // Même règle que le déverrouillage de /admin (useAdminAccessControl) : au
  // moins un établissement ACTIVATED. Aucun établissement : l'étape pointe vers
  // la création ; établissements présents mais aucun actif : vers leur gestion.
  const step3Done = hasValidEstablishment;

  const step1Status = step1Done ? "done" : "current";
  const step2Status = step2Done ? "done" : step1Done ? "current" : "locked";
  // Créer un établissement n'exige pas un organisme COMPLETED : l'étape 3 est
  // actionnable dès que le profil est activé et qu'un organisme existe.
  const step3Status = step3Done ? "done" : step1Done && hasOrganization ? "current" : "locked";

  const completedCount = [step1Done, step2Done, step3Done].filter(Boolean).length;
  const progressPercent = Math.max(10, Math.round((completedCount / 3) * 100));

  // L'en-tête reflète toujours la première étape non finalisée (jamais la
  // dernière validée) et reste dans le ton "ambré" tant qu'il en reste une :
  // ce composant ne s'affiche de toute façon que lorsqu'au moins une étape
  // est encore à faire.
  const currentStep = !step1Done ? 1 : !step2Done ? 2 : !step3Done ? 3 : null;
  const bannerTag =
    currentStep === 3
      ? t("accountActivation.banner.tagEstablishments", "Établissements requis")
      : currentStep === 2
        ? hasOrganization
          ? t("accountActivation.banner.tagOrganizationIncomplete", "Organisme à compléter")
          : t("accountActivation.banner.tagOrganization", "Organisme requis")
        : t("accountActivation.banner.tagRequired", "Activation requise");
  const bannerTitle =
    currentStep === 3
      ? t("accountActivation.banner.titleEstablishments", "Accès à vos établissements requis")
      : currentStep === 2
        ? hasOrganization
          ? t("accountActivation.banner.titleOrganizationIncomplete", "Complétez votre organisme")
          : t("accountActivation.banner.titleOrganization", "Création de votre organisme requise")
        : t("accountActivation.banner.titleRequired", "Activation du compte et de l'organisme");
  const bannerDescription =
    currentStep === 3
      ? t(
          "accountActivation.banner.descriptionEstablishments",
          "Un établissement actif est requis pour accéder à l'administration."
        )
      : currentStep === 2
        ? hasOrganization
          ? t(
              "accountActivation.banner.descriptionOrganizationIncomplete",
              "Renseignez les informations manquantes de votre organisme pour le valider."
            )
          : t(
              "accountActivation.banner.descriptionOrganization",
              "Votre profil est activé. Créez votre organisme pour accéder à l'administration."
            )
        : t(
            "accountActivation.banner.descriptionRequired",
            "Veuillez compléter les étapes ci-dessous pour activer votre espace d'administration."
          );

  // Évite d'afficher « Non créé » / « À compléter » le temps de lire le statut.
  if (organization?.entityId && organismeLoading && !organisme) {
    return (
      <div className={styles.root}>
        <Preloader />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <section className={styles.card}>
        <div className={[styles.banner, styles["banner-warning"]].join(" ")}>
          <span className={[styles.bannerIcon, styles["bannerIcon-warning"]].join(" ")}>
            <Icon name="iconWarning" size="lg" color="inherit" />
          </span>

          <div className={styles.bannerText}>
            <div className={styles.bannerTagRow}>
              <span className={[styles.bannerTag, styles["bannerTag-warning"]].join(" ")}>
                {bannerTag}
              </span>
            </div>
            <Text as="h3" size="lg" weight="bold">
              {bannerTitle}
            </Text>
            <Text as="p" size="xs" color="muted">
              {bannerDescription}
            </Text>
          </div>

          <div className={styles.progress}>
            <div className={styles.progressText}>
              <Text as="span" size="xs" weight="bold">
                {t("accountActivation.progress.count", "{{count}} sur 3 complétées", {
                  count: completedCount,
                })}
              </Text>
              <Text as="span" size="xs" color="muted">
                {t("accountActivation.progress.label", "Progression requise")}
              </Text>
            </div>
            <div className={styles.progressTrack}>
              <div
                className={[styles.progressBar, styles["progressBar-warning"]].join(" ")}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        <div className={styles.steps}>
          <Text as="h4" size="xs" weight="bold" color="muted" className={styles.stepsTitle}>
            {t("accountActivation.stepsTitle", "Parcours de validation")}
          </Text>

          <StepCard
            status={step1Status}
            ctaIcon="iconArrowRight"
            stepLabel={t("accountActivation.step1.label", "Étape 1 · Requis")}
            tagLabel={
              step1Done
                ? t("accountActivation.step1.tagDone", "ACTIVATED")
                : t("accountActivation.step1.tagPending", "MISSING_INFORMATIONS")
            }
            title={t("accountActivation.step1.title", "Activer votre profil personnel")}
            description={t(
              "accountActivation.step1.description",
              "Renseignez votre date de naissance et votre numéro de téléphone pour activer votre compte."
            )}
            ctaLabel={
              step1Done
                ? t("accountActivation.step1.ctaDone", "Modifier le profil")
                : t("accountActivation.step1.cta", "Compléter le profil")
            }
            onCta={() => navigate("/profil")}
          />

          <StepCard
            status={step2Status}
            ctaIcon="iconBank"
            stepLabel={t("accountActivation.step2.label", "Étape 2")}
            tagLabel={
              step2Done
                ? t("accountActivation.step2.tagDone", "Créé")
                : hasOrganization
                  ? t("accountActivation.step2.tagIncomplete", "À compléter")
                  : t("accountActivation.step2.tagPending", "Non créé")
            }
            title={t("accountActivation.step2.title", "Créer votre organisme")}
            description={t(
              "accountActivation.step2.description",
              "Renseignez la raison sociale ainsi que l'adresse officielle de votre structure."
            )}
            ctaLabel={
              step2Done
                ? t("accountActivation.step2.ctaDone", "Gérer l'organisme")
                : hasOrganization
                  ? t("accountActivation.step2.ctaComplete", "Compléter l'organisme")
                  : t("accountActivation.step2.cta", "Créer l'organisme")
            }
            onCta={() => navigate(hasOrganization ? "/admin/organisme" : "/admin/organisme/add")}
          />

          <StepCard
            status={step3Status}
            ctaIcon="iconPlus"
            stepLabel={t("accountActivation.step3.label", "Étape 3")}
            tagLabel={
              step3Done
                ? t("accountActivation.step3.tagDone", "Configuré")
                : t("accountActivation.step3.tagPending", "Non configuré")
            }
            title={t("accountActivation.step3.title", "Accéder à vos établissements")}
            description={
              hasEstablishments
                ? t(
                    "accountActivation.step3.descriptionPending",
                    "Aucun de vos établissements n'est encore actif. Complétez-les pour débloquer l'accès à l'administration."
                  )
                : t(
                    "accountActivation.step3.description",
                    "Créez votre premier établissement pour débloquer l'accès à l'administration."
                  )
            }
            ctaLabel={
              hasEstablishments
                ? t("accountActivation.step3.ctaDone", "Gérer les établissements")
                : t("accountActivation.step3.ctaCreate", "Créer un établissement")
            }
            onCta={() =>
              navigate(
                hasEstablishments ? "/admin/establishments" : "/admin/establishments/add"
              )
            }
          />
        </div>

        <div className={styles.footer}>
          <span className={styles.footerHint}>
            <Icon name="iconQuestion" size="sm" />
            {t(
              "accountActivation.footer",
              "Besoin d'assistance ? Contactez votre gestionnaire de compte."
            )}
          </span>
        </div>
      </section>
    </div>
  );
};

export default AccountActivationRequired;
